/**
 * Pure pdfMake docDefinition builder for ad/job receipts.
 * Kept free of React so it can be self-checked from Node
 * via buildAdJobReceiptDoc.check.js (not imported by the app).
 */

const safePdfText = (value, fallback = 'N/A') => {
  if (value === null || value === undefined || value === '') return fallback;
  return String(value);
};

const stripHtml = (html) => {
  if (!html) return '';
  return String(html)
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const resolveAdJobReceiptAmount = (type, item) => {
  const paid = Number(item?.payment?.paid_amount);
  if (!Number.isNaN(paid) && item?.payment?.paid_amount != null) return paid;
  const amount = Number(item?.payment?.amount);
  if (!Number.isNaN(amount) && item?.payment?.amount != null) return amount;
  const fee = Number(
    type === 'job' ? item?.job_fee ?? item?.jobFees : item?.ad_fee,
  );
  return Number.isNaN(fee) ? 0 : fee;
};

const labeledRow = (label, value) => [
  {
    text: [
      {text: `${label}: `, bold: true},
      typeof value === 'object' && value !== null ? value : safePdfText(value),
    ],
    colSpan: 2,
  },
  {},
];

const buildAdJobReceiptDocDefinition = ({
  type,
  item,
  strings = {},
  logoDataUrl,
  formatCurrency = (v) => `$${Number(v || 0).toFixed(2)}`,
  formatDate = (v) => (v ? String(v) : 'N/A'),
  formatPaymentDate = (v) => (v ? String(v) : 'N/A'),
  formatListingDate = (v) => (v ? String(v) : 'N/A'),
}) => {
  const isJob = type === 'job';
  const title = isJob
    ? strings.jobReceipt || 'Job Receipt'
    : strings.adReceipt || 'Ad Receipt';
  // Headline under title: job position, or ad vendor name.
  const headline = isJob
    ? item?.position || item?.jobTitle
    : item?.vendor || item?.adsHeading;
  const advertiserNameLabel = strings.advertiserName || 'Advertiser Name';
  const positionDesignationLabel =
    strings.positionDesignation || 'Position / Designation';
  const months = item?.ad_months ?? item?.job_months ?? item?.months;
  const fee = resolveAdJobReceiptAmount(type, item);
  const currency = item?.payment?.currency_type || 'USD';
  const payment = item?.payment;
  const paymentRaw = payment?.payment_datetime ?? payment?.payment_date;
  // Listing placement window (start/end), not payment date.
  const startDate =
    item?.start_date || item?.adsStart_date || item?.jobStartDate;
  const endDate = item?.end_date || item?.adsEnd_date || item?.jobLastDate;
  const description = stripHtml(
    item?.description || item?.adsDescription || item?.jobDescription,
  );
  const requirement = stripHtml(
    item?.requirement || item?.jobRequirements,
  );
  const attachmentUrl =
    item?.attachment_url || item?.url || item?.jobUrl || '';
  const email = item?.email || item?.adsEmail || '';
  const contactInfo = item?.contact_info || item?.jobContactInfo || '';
  const location = item?.location || item?.jobLocation || '';
  const feeNote = isJob
    ? item?.job_fee_note
    : item?.ad_fee_note;
  const adTypeFee = Number(item?.ad_fee);
  const adTypePrice =
    item?.ad_fee != null && !Number.isNaN(adTypeFee)
      ? `(${formatCurrency(adTypeFee, currency)})`
      : '';
  const adTypeValue = [feeNote, adTypePrice].filter(Boolean).join(' ') || null;
  const employer = item?.employer || item?.jobEmployer;
  const listingPeriodRange =
    startDate || endDate
      ? `${formatListingDate(startDate)} to ${formatListingDate(endDate)}`
      : null;
  const notApprovedYetText = isJob
    ? strings.jobNotApprovedYet || 'Job is not approved yet'
    : strings.adNotApprovedYet || 'Ad is not approved yet';
  const listingPeriodValue = listingPeriodRange
    ? listingPeriodRange
    : {text: notApprovedYetText, color: 'red'};

  // Mirror active JobsForm / AdsForm fields on the receipt.
  const detailRows = isJob
    ? [
        labeledRow(strings.employer || 'Employer', employer),
        labeledRow(strings.location || 'Location', location),
        labeledRow(
          strings.contactInfo || 'Contact Information',
          contactInfo,
        ),
        labeledRow(
          strings.attachmentUrl || 'Attachment URL',
          attachmentUrl,
        ),
        labeledRow(strings.jobType || 'Job Type', feeNote),
        labeledRow(strings.months || 'Duration (months)', months),
        labeledRow(
          strings.listingPeriod || 'Listing period',
          listingPeriodValue,
        ),
      ]
    : [
        labeledRow(strings.email || 'Email', email),
        labeledRow(
          strings.attachmentUrl || 'Attachment URL',
          attachmentUrl,
        ),
        labeledRow(strings.adType || 'Ad Type', adTypeValue),
        labeledRow(strings.months || 'Duration (months)', months),
        labeledRow(
          strings.listingPeriod || 'Listing period',
          listingPeriodValue,
        ),
      ];

  const paymentDetailsBody = [
    [
      {text: strings.totalPrice || 'Total Price', bold: true},
      {text: formatCurrency(fee, currency), alignment: 'right', bold: true},
    ],
  ];

  const content = [
    {
      columns: [
        {
          width: '*',
          stack: [
            {text: title, style: 'title'},
            {
              text: isJob ? positionDesignationLabel : advertiserNameLabel,
              style: 'subLabel',
              margin: [0, 8, 0, 2],
            },
            {
              text: safePdfText(headline),
              style: 'headline',
            },
          ],
        },
        {
          width: 150,
          stack: [
            logoDataUrl
              ? {image: logoDataUrl, width: 70, alignment: 'right'}
              : {text: '', width: 70},
            {
              text: strings.logoText || 'Pharmaceutical & BioScience Society',
              alignment: 'right',
              fontSize: 8,
              margin: [0, 5, 0, 0],
            },
          ],
        },
      ],
    },
    {
      canvas: [
        {
          type: 'line',
          x1: 0,
          y1: 0,
          x2: 515,
          y2: 0,
          lineColor: '#d9d9d9',
        },
      ],
      margin: [0, 14, 0, 10],
    },
    {
      table: {
        widths: ['*', '*'],
        body: detailRows,
      },
      layout: 'noBorders',
      margin: [0, 0, 0, 6],
    },
  ];

  if (description) {
    content.push({
      text: [
        {
          text: `${strings.description || 'Description'}: `,
          bold: true,
        },
        description,
      ],
      fontSize: 9,
      color: '#555555',
      margin: [0, 0, 0, isJob && requirement ? 6 : 14],
    });
  }

  if (isJob && requirement) {
    content.push({
      text: [
        {
          text: `${strings.requirement || 'Requirements'}: `,
          bold: true,
        },
        requirement,
      ],
      fontSize: 9,
      color: '#555555',
      margin: [0, 0, 0, 14],
    });
  }

  content.push(
    {
      text: strings.paymentDetails || 'Payment Details',
      style: 'paymentTitle',
      margin: [0, 8, 0, 6],
    },
    {
      text: [
        {
          text: `${strings.paymentDate || 'Payment Date'}: `,
          bold: true,
        },
        safePdfText(formatPaymentDate(paymentRaw)),
        payment?.card_last4
          ? `  ·  ${
              strings.cardLast4 || 'Credit Card Number'
            }: **** **** **** ${payment.card_last4}`
          : '',
      ],
      margin: [0, 0, 0, 8],
      fontSize: 9,
    },
    {
      table: {
        widths: ['*', 120],
        body: paymentDetailsBody,
      },
      layout: {
        hLineColor: () => '#d9d9d9',
        vLineColor: () => '#d9d9d9',
        paddingLeft: () => 6,
        paddingRight: () => 6,
        paddingTop: () => 5,
        paddingBottom: () => 5,
      },
    },
  );

  return {
    pageSize: 'A4',
    pageOrientation: 'portrait',
    pageMargins: [40, 40, 40, 40],
    defaultStyle: {
      font: 'Inter',
      fontSize: 10,
    },
    content,
    styles: {
      title: {fontSize: 18, bold: true},
      headline: {fontSize: 11, bold: true},
      subLabel: {fontSize: 10, bold: true},
      paymentTitle: {fontSize: 12, bold: true},
    },
    meta: {
      filename: `${isJob ? 'job' : 'ad'}-receipt-${
        item?.id || item?.jobId || 'download'
      }.pdf`,
      amount: fee,
      currency,
    },
  };
};

module.exports = {
  resolveAdJobReceiptAmount,
  buildAdJobReceiptDocDefinition,
};
